var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _this = this;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useRef, useState, } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { useConfig } from "@context/config";
import { useGoogleMaps } from "@hooks/google-maps";
var CONTAINER_STYLE = {
    width: "100%",
    height: "100%",
};
// Copenhagen
var CENTER = {
    lat: 55.6683071,
    lng: 12.4762091,
};
var MAP_OPTIONS = {
    styles: [
        {
            featureType: "administrative",
            elementType: "labels.text.fill",
            stylers: [
                {
                    color: "#444444",
                },
            ],
        },
        {
            featureType: "landscape",
            elementType: "all",
            stylers: [
                {
                    color: "#eaeaea",
                },
            ],
        },
        {
            featureType: "poi",
            elementType: "all",
            stylers: [
                {
                    visibility: "off",
                },
            ],
        },
        {
            featureType: "road",
            elementType: "all",
            stylers: [
                {
                    saturation: -100,
                },
                {
                    lightness: 45,
                },
            ],
        },
        {
            featureType: "road.highway",
            elementType: "all",
            stylers: [
                {
                    visibility: "simplified",
                },
            ],
        },
        {
            featureType: "road.arterial",
            elementType: "labels.icon",
            stylers: [
                {
                    visibility: "off",
                },
            ],
        },
        {
            featureType: "transit",
            elementType: "all",
            stylers: [
                {
                    visibility: "off",
                },
            ],
        },
        {
            featureType: "water",
            elementType: "all",
            stylers: [
                {
                    color: "#283583",
                },
                {
                    visibility: "on",
                },
            ],
        },
    ],
};
var PIN_ICON = {
    path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
    fillColor: "#363636",
    strokeColor: "#fff",
    fillOpacity: 1,
    strokeWeight: 1,
    scale: 2,
};
var HOME_ICON = {
    path: "M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z",
    fillColor: "#fabb04",
    fillOpacity: 1,
    strokeColor: "#fff",
    strokeWeight: 1,
    scale: 1.5,
};
export var MapSkeleton = function () {
    return (_jsx("div", { className: "w-full h-full animate-pulse opacity-40 bg-decent" }, void 0));
};
var getLatLng = function (_a) {
    var lat = _a.lat, lng = _a.lng;
    return {
        lat: parseFloat(lat),
        lng: parseFloat(lng),
    };
};
var Map = function (_a) {
    var locations = _a.locations, selected = _a.selected, setSelected = _a.setSelected, coords = _a.coords;
    var defaultLocation = useConfig().defaultLocation;
    var _b = useGoogleMaps(), isLoaded = _b.isLoaded, loadError = _b.loadError;
    var renderMap = function () {
        var _a = useState(null), map = _a[0], setMap = _a[1];
        var center = useRef(__assign(__assign({}, CENTER), defaultLocation));
        var onLoad = useCallback(function callback(map) {
            setMap(map);
        }, []);
        var handleClick = function (index) {
            setSelected(index);
        };
        useEffect(function () {
            if (map &&
                coords &&
                coords.latitude !== null &&
                coords.longitude !== null) {
                var latitude = coords.latitude, longitude = coords.longitude;
                map.panTo({ lat: latitude, lng: longitude });
            }
        }, [coords]);
        useEffect(function () {
            if (map && selected >= 0 && selected < locations.length) {
                map.panTo(getLatLng(locations[selected].coordinates));
            }
        }, [selected]);
        useEffect(function () {
            return function () {
                setMap(null);
            };
        }, []);
        return (_jsxs(GoogleMap, __assign({ mapContainerStyle: CONTAINER_STYLE, center: center.current, zoom: 11, onLoad: onLoad, options: MAP_OPTIONS }, { children: [locations.map(function (_a, index) {
                    var id = _a.id, coordinates = _a.coordinates;
                    return (_jsx(Marker, { onClick: handleClick.bind(_this, index), position: getLatLng(coordinates), icon: __assign(__assign({}, PIN_ICON), { fillColor: index === selected ? "#e94335" : "#363636", origin: new window.google.maps.Point(0, 0), anchor: new window.google.maps.Point(12, 24) }) }, id));
                }),
                coords &&
                    coords.latitude !== null &&
                    coords.longitude !== null && (_jsx(Marker, { position: {
                        lat: coords.latitude,
                        lng: coords.longitude,
                    }, icon: __assign(__assign({}, HOME_ICON), { origin: new window.google.maps.Point(0, 0), anchor: new window.google.maps.Point(12, 24) }) }, "home-icon"))] }), void 0));
    };
    if (loadError) {
        return (_jsx("div", __assign({ className: "text-center pt-40" }, { children: "Map cannot be loaded right now, sorry." }), void 0));
    }
    return isLoaded ? renderMap() : _jsx(MapSkeleton, {}, void 0);
};
export default memo(Map, function (prev, next) {
    if (prev.selected !== next.selected) {
        return false;
    }
    if (prev.locations.length !== next.locations.length) {
        return false;
    }
    if (JSON.stringify(prev.coords) !== JSON.stringify(next.coords)) {
        return false;
    }
    return true;
});
